import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import ChangePasswordDialog from './ChangePasswordDialog';
import { logout } from '../containers/User/AuthSlice';
import { AMBER, BRAND_FONT, LIGHT, useIsMobile } from '../theme';

// Initials for the avatar, so the bar stays compact on a phone where the full
// name and email do not fit.
const initials = (name = '', email = '') => {
    const source = (name || email || '?').trim();
    const parts = source.split(/[\s@._-]+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

export default function Header() {
    const user = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const [anchorEl, setAnchorEl] = useState(null);
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);

    const handleLogout = () => {
        setAnchorEl(null);
        dispatch(logout());
    };

    const handleChangePassword = () => {
        setAnchorEl(null);
        setPasswordOpen(true);
    };

    return (
        <AppBar position='sticky' elevation={0} sx={{ bgcolor: 'primary.main', color: LIGHT }}>
            <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 } }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: 1, minWidth: 0 }}>
                    <Typography
                        component='h1'
                        noWrap
                        sx={{
                            fontFamily: BRAND_FONT,
                            // Scales with the viewport instead of the old fixed
                            // 40pt, which overflowed every phone.
                            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.1rem' },
                            lineHeight: 1.2,
                            color: LIGHT,
                        }}
                    >
                        Disco de Vinil
                    </Typography>
                    <Typography
                        component='span'
                        sx={{
                            color: AMBER,
                            fontStyle: 'italic',
                            fontSize: { xs: '0.65rem', sm: '0.8rem' },
                            letterSpacing: '0.08em',
                        }}
                    >
                        BACKOFFICE
                    </Typography>
                </Box>

                {user && (
                    <>
                        {/* Desktop: name + email inline. Phone: just the avatar,
                            with the details inside the menu it opens. */}
                        {!isMobile && (
                            <Box sx={{ textAlign: 'right', lineHeight: 1.2, mr: 0.5, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: '0.85rem' }}>
                                    {user.name}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: '0.72rem', opacity: 0.7 }}>
                                    {user.email}
                                </Typography>
                            </Box>
                        )}
                        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label='Conta' sx={{ color: LIGHT }}>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: AMBER, color: '#191716', fontSize: '0.85rem', fontWeight: 700 }}>
                                {initials(user.name, user.email)}
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <Box sx={{ px: 2, py: 1, maxWidth: 260 }}>
                                <Typography sx={{ fontWeight: 600 }} noWrap>
                                    {user.name}
                                </Typography>
                                <Typography variant='body2' color='text.secondary' noWrap>
                                    {user.email}
                                </Typography>
                            </Box>
                            <Divider />
                            <MenuItem onClick={handleChangePassword}>
                                <ListItemIcon>
                                    <LockResetIcon fontSize='small' />
                                </ListItemIcon>
                                Alterar palavra-passe
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize='small' />
                                </ListItemIcon>
                                Terminar sessão
                            </MenuItem>
                        </Menu>

                        <ChangePasswordDialog
                            open={passwordOpen}
                            onClose={() => setPasswordOpen(false)}
                            onSuccess={() => setPasswordChanged(true)}
                        />

                        <Snackbar
                            open={passwordChanged}
                            autoHideDuration={6000}
                            onClose={() => setPasswordChanged(false)}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <Alert onClose={() => setPasswordChanged(false)} severity='success' variant='filled' sx={{ width: '100%' }}>
                                Palavra-passe alterada.
                            </Alert>
                        </Snackbar>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
}
